/**
 * Client-side password handling:
 * Passwords are sent to the server over HTTPS for hashing and storage.
 */
export class AuthUtils {
  /**
   * Generate legacy SHA-256 password hash expected by older auth API variants.
   */
  static async toLegacyPasswordHash(password: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const bytes = Array.from(new Uint8Array(digest));
    return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Complete client-side password processing for registration/login
   * Returns password as-is; hashing happens server-side.
   */
  static async processPassword(password: string): Promise<string> {
    return password;
  }

  /**
   * Validate password strength
   */
  static validatePassword(password: string): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Heslo musí mít alespoň 8 znaků');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Heslo musí obsahovat alespoň jedno velké písmeno');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Heslo musí obsahovat alespoň jedno malé písmeno');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Heslo musí obsahovat alespoň jednu číslici');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Heslo musí obsahovat alespoň jeden speciální znak');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate email format
   */
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone number format (Czech: +420XXXXXXXXX, 9-15 digits)
   */
  static validatePhone(phone: string): boolean {
    const stripped = phone.replace(/\s/g, '');
    return /^\+?[0-9]{9,15}$/.test(stripped);
  }

  /**
   * Sanitize username (remove special chars, preserve case)
   */
  static sanitizeUsername(username: string): string {
    return username
      .replace(/[^a-zA-Z0-9._-]/g, '')
      .substring(0, 50); // Max length 50
  }
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterCredentials {
  username: string;
  email: string;
  phone: string;
  promoCode?: string;
  password: string;
  confirmPassword: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  user?: {
    id: string;
    username: string;
    email: string;
  };
}
